import {BRAND_APP_FOOTER, BRAND_NAME, BRAND_PRODUCT} from './brand.js';

/**
 * 渲染原力引擎产品官网首页
 * @param root 页面根节点
 */
export function renderMarketingPage(root: HTMLElement): void {
  document.title = `${BRAND_NAME} · ${BRAND_PRODUCT}`;

  root.innerHTML = `<div class="marketing-site oai-theme">
    <div class="oai-ambient-glow" aria-hidden="true"></div>

    <header class="oai-header">
      <div class="oai-header-inner">
        <a class="oai-brand" href="/" aria-label="${BRAND_NAME} 首页">
          <div class="oai-brand-mark">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <span class="oai-brand-name">${BRAND_NAME}</span>
        </a>

        <nav class="oai-nav" id="oai-mobile-nav" aria-label="核心导航">
          <a href="#workspace">工作台</a>
          <a href="#capabilities">能力</a>
          <a href="#pipeline">流程</a>
          <a href="#solutions">适用场景</a>
        </nav>

        <div class="oai-header-actions">
          <a class="oai-btn oai-btn-ghost" href="/login">登录</a>
          <a class="oai-btn oai-btn-primary" href="/register">
            <span class="oai-btn-text-full">注册账号</span>
            <span class="oai-btn-text-short">注册</span>
          </a>
          <button type="button" class="oai-nav-toggle" aria-label="打开菜单" aria-expanded="false" aria-controls="oai-mobile-nav">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 7H20M4 12H20M4 17H20" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </header>

    <main class="oai-main">
      <section class="oai-hero">
        <div class="oai-hero-inner">
          <h1 class="oai-hero-title">
            把重复的办公工作<br>
            <span class="oai-gradient-text">交给智能引擎自动完成</span>
          </h1>

          <p class="oai-hero-desc">
            ${BRAND_NAME} 帮助企业把单据识别、数据核对、报告生成与团队协同，整合进同一套智能工作台。少录入、少切换、少出错，让人专注判断与决策。
          </p>

          <div class="oai-hero-cta">
            <a class="oai-btn oai-btn-primary oai-btn-xl" href="/register">免费开始使用</a>
            <a class="oai-btn oai-btn-glass oai-btn-xl" href="/login">进入工作台</a>
          </div>

          <div class="oai-hero-stats">
            <div class="oai-stat-item">
              <strong>识别</strong>
              <span>单据自动结构化</span>
            </div>
            <div class="oai-stat-divider"></div>
            <div class="oai-stat-item">
              <strong>核对</strong>
              <span>差异即时提示</span>
            </div>
            <div class="oai-stat-divider"></div>
            <div class="oai-stat-item">
              <strong>生成</strong>
              <span>报告与台账一键输出</span>
            </div>
            <div class="oai-stat-divider"></div>
            <div class="oai-stat-item">
              <strong>协同</strong>
              <span>团队空间安全共享</span>
            </div>
          </div>
        </div>
      </section>

      <section class="oai-section oai-demo-section" id="workspace">
        <div class="oai-section-header">
          <p class="oai-section-tag">工作台</p>
          <h2 class="oai-section-title">一个工作台，完成从识别到交付</h2>
          <p class="oai-section-sub">原始文件、抽取结果、核对差异与最终输出，在同一界面联动，不再在多个软件间来回复制。</p>
        </div>

        <div class="oai-interactive-showcase">
          <div class="oai-demo-tabs" role="tablist">
            <button type="button" class="oai-demo-tab is-active" data-demo-target="ocr">
              <span class="oai-tab-text">单据智能识别</span>
            </button>
            <button type="button" class="oai-demo-tab" data-demo-target="matching">
              <span class="oai-tab-text">数据自动核对</span>
            </button>
            <button type="button" class="oai-demo-tab" data-demo-target="ledger">
              <span class="oai-tab-text">报告一键生成</span>
            </button>
            <button type="button" class="oai-demo-tab" data-demo-target="legal">
              <span class="oai-tab-text">合同智能审查</span>
            </button>
          </div>

          <div class="oai-workspace-monitor">
            <div class="oai-monitor-chrome">
              <div class="oai-monitor-title">${BRAND_NAME} 工作台</div>
            </div>

            <div class="oai-monitor-viewport">
              <div class="oai-demo-panel is-active" id="demo-ocr">
                <div class="oai-sim-grid">
                  <div class="oai-sim-doc">
                    <div class="oai-sim-doc-header">
                      <span class="oai-sim-pill">业务单据 · PDF / 扫描件</span>
                      <span class="oai-sim-status is-ok">识别完成</span>
                    </div>
                    <div class="oai-sim-doc-body">
                      <div class="oai-sim-doc-line hl-field">单据类型：<strong>发票 / 报关单 / 合同附件</strong></div>
                      <div class="oai-sim-doc-line hl-field">关键字段：<strong>自动定位并高亮</strong></div>
                      <div class="oai-sim-doc-line hl-field">版式处理：<strong>纠偏、去噪、跨行对齐</strong></div>
                      <div class="oai-sim-doc-box">
                        <div class="oai-box-row">上传后自动抽取结构化数据</div>
                        <div class="oai-box-row">低置信度字段单独提示复核</div>
                        <div class="oai-box-row">结果可直接进入核对与生成环节</div>
                      </div>
                    </div>
                  </div>
                  <div class="oai-sim-extractor">
                    <div class="oai-sim-col-title">结构化结果预览</div>
                    <div class="oai-sim-table">
                      <div class="oai-st-row oai-st-head"><span>字段</span><span>取值</span><span>状态</span></div>
                      <div class="oai-st-row"><span>单据编号</span><code>自动提取</code><span class="oai-score">已就绪</span></div>
                      <div class="oai-st-row"><span>往来单位</span><span>自动识别</span><span class="oai-score">已就绪</span></div>
                      <div class="oai-st-row"><span>金额 / 数量</span><code>自动校验</code><span class="oai-score">已就绪</span></div>
                      <div class="oai-st-row"><span>日期信息</span><code>标准化输出</code><span class="oai-score">已就绪</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="oai-demo-panel" id="demo-matching">
                <div class="oai-sim-matching-view">
                  <div class="oai-match-head">
                    <div class="oai-match-summary">
                      <span class="oai-match-badge is-done">✓ 核对通过</span>
                      <span>两侧数据自动对齐，差异项即时标出</span>
                    </div>
                  </div>
                  <div class="oai-match-columns">
                    <div class="oai-match-card">
                      <div class="oai-mc-title">来源单据 A</div>
                      <div class="oai-mc-item is-linked">
                        <strong>商品 / 费用项 #1</strong>
                        <small>数量、金额、币种已对齐</small>
                      </div>
                    </div>
                    <div class="oai-match-link-indicator">
                      <span>智能匹配</span>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#8ABCD1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </div>
                    <div class="oai-match-card">
                      <div class="oai-mc-title">来源单据 B</div>
                      <div class="oai-mc-item is-linked">
                        <strong>对应明细行</strong>
                        <small>自动关联，尾差为 0</small>
                      </div>
                      <div class="oai-mc-item is-linked">
                        <strong>补充明细行</strong>
                        <small>一对多 / 多对多均可处理</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="oai-demo-panel" id="demo-ledger">
                <div class="oai-sim-ledger-view">
                  <div class="oai-ledger-topbar">
                    <span class="oai-ledger-tag">按企业模板方案自动计算并导出</span>
                    <button class="oai-btn-sm" type="button">下载 Excel</button>
                  </div>
                  <div class="oai-ledger-grid-preview">
                    <div class="oai-lg-row oai-lg-head">
                      <span>序号</span><span>关联单据</span><span>项目名称</span><span>金额</span><span>公式结果</span><span>状态</span>
                    </div>
                    <div class="oai-lg-row">
                      <span>1</span><span>DOC-2026-01</span><span>示例项目 A</span><span>¥68,438.50</span><span>¥68,438.50</span><span class="oai-tag-success">已生成</span>
                    </div>
                    <div class="oai-lg-row">
                      <span>2</span><span>DOC-2026-02</span><span>示例项目 B</span><span>¥45,190.20</span><span>¥45,190.20</span><span class="oai-tag-success">已生成</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="oai-demo-panel" id="demo-legal">
                <div class="oai-sim-legal-view">
                  <div class="oai-legal-pane">
                    <h4>合同原文</h4>
                    <p class="oai-legal-clause hl-risk">第 8.3 条：一方可在特定条件下单方面延长付款周期，且未约定对等补偿机制……</p>
                  </div>
                  <div class="oai-legal-pane is-analysis">
                    <div class="oai-risk-badge">风险已标注</div>
                    <p class="oai-risk-desc"><strong>审查建议：</strong>该条款权利义务不对称，建议补充对等调节与免责边界，降低后续履约争议。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="oai-section" id="capabilities">
        <div class="oai-section-header">
          <p class="oai-section-tag">能力</p>
          <h2 class="oai-section-title">为日常办公自动化准备的核心能力</h2>
          <p class="oai-section-sub">识别、核对、生成、协同、合规——覆盖企业高频重复工作，而不是只服务某一行业。</p>
        </div>

        <div class="oai-bento-grid">
          <article class="oai-card oai-card-wide">
            <div class="oai-card-copy">
              <span class="oai-card-tag">智能识别</span>
              <h3>把各类办公单据变成可用数据</h3>
              <p>支持发票、合同、业务单证等常见文件。自动处理倾斜、反光与印章遮挡，抽取关键字段，减少人工逐项录入。</p>
            </div>
            <div class="oai-card-visual doc-glow-visual">
              <div class="oai-chip-flow">
                <span class="oai-chip">版式自适应</span>
                <span class="oai-chip">字段结构化</span>
                <span class="oai-chip">低置信度提示</span>
              </div>
            </div>
          </article>

          <article class="oai-card">
            <div class="oai-card-copy">
              <span class="oai-card-tag">自动核对</span>
              <h3>多份材料自动对齐</h3>
              <p>按数量、金额、币种等规则交叉比对，差异项即时标出，少漏核、少反复沟通。</p>
            </div>
          </article>

          <article class="oai-card">
            <div class="oai-card-copy">
              <span class="oai-card-tag">报告生成</span>
              <h3>兼容企业既有模板</h3>
              <p>绑定字段映射与计算公式，一键输出可交付的 Excel 台账或业务报告。</p>
            </div>
          </article>

          <article class="oai-card">
            <div class="oai-card-copy">
              <span class="oai-card-tag">团队协同</span>
              <h3>团队空间清晰可控</h3>
              <p>个人与企业文件分区管理，共享与上传职责分明，权限与归属一目了然。</p>
            </div>
          </article>

          <article class="oai-card oai-card-wide">
            <div class="oai-card-copy">
              <span class="oai-card-tag">合规与留存</span>
              <h3>合同审查与原件生命周期管理</h3>
              <p>合同条款可自动批注风险；识别原件支持按任务设定保留天数，到期自动清理，兼顾效率与合规。</p>
            </div>
          </article>
        </div>
      </section>

      <section class="oai-section oai-pipeline-section" id="pipeline">
        <div class="oai-section-header">
          <p class="oai-section-tag">流程</p>
          <h2 class="oai-section-title">五步完成一次智能办公闭环</h2>
          <p class="oai-section-sub">从上传到归档，流程固定、结果可追溯。</p>
        </div>

        <div class="oai-pipeline-track">
          <div class="oai-pipe-step">
            <div class="oai-pipe-num">01</div>
            <h4>上传文件</h4>
            <p>批量导入 PDF、扫描件与业务附件</p>
          </div>
          <div class="oai-pipe-arrow">→</div>
          <div class="oai-pipe-step">
            <div class="oai-pipe-num">02</div>
            <h4>智能识别</h4>
            <p>自动抽取字段，生成结构化结果</p>
          </div>
          <div class="oai-pipe-arrow">→</div>
          <div class="oai-pipe-step">
            <div class="oai-pipe-num">03</div>
            <h4>自动核对</h4>
            <p>跨单据比对，标出差异与风险</p>
          </div>
          <div class="oai-pipe-arrow">→</div>
          <div class="oai-pipe-step">
            <div class="oai-pipe-num">04</div>
            <h4>生成交付物</h4>
            <p>按方案输出报告、台账或导出文件</p>
          </div>
          <div class="oai-pipe-arrow">→</div>
          <div class="oai-pipe-step">
            <div class="oai-pipe-num">05</div>
            <h4>协同归档</h4>
            <p>沉淀到团队空间，原件按策略清理</p>
          </div>
        </div>
      </section>

      <section class="oai-section" id="solutions">
        <div class="oai-section-header">
          <p class="oai-section-tag">适用场景</p>
          <h2 class="oai-section-title">适合需要减少重复劳动的团队</h2>
          <p class="oai-section-sub">无论财务、运营还是合规，只要工作里充满单据与核对，就能直接上手。</p>
        </div>

        <div class="oai-solutions-grid">
          <article class="oai-sol-card">
            <div class="oai-sol-icon">📑</div>
            <h3>财务与运营团队</h3>
            <p>发票、对账单、业务明细自动识别与核对，缩短月末结账与日常对账时间。</p>
          </article>
          <article class="oai-sol-card">
            <div class="oai-sol-icon">🏢</div>
            <h3>企业中后台</h3>
            <p>多成员协作、团队空间共享与任务并行，把分散材料收敛到统一工作台。</p>
          </article>
          <article class="oai-sol-card">
            <div class="oai-sol-icon">🛡️</div>
            <h3>法务与合规岗</h3>
            <p>合同风险自动标注，审核依据可追溯；原件留存策略可控，降低合规压力。</p>
          </article>
        </div>
      </section>
    </main>

    <footer class="oai-footer">
      <div class="oai-footer-inner">
        <div class="oai-footer-top">
          <div class="oai-footer-brand">
            <span class="oai-footer-logo">${BRAND_NAME}</span>
            <p>${BRAND_PRODUCT} · 让重复工作自动完成。</p>
          </div>
          <div class="oai-footer-links">
            <div class="oai-link-group">
              <strong>产品</strong>
              <a href="#capabilities">智能识别</a>
              <a href="#capabilities">自动核对</a>
              <a href="#capabilities">报告生成</a>
              <a href="#capabilities">合同审查</a>
            </div>
            <div class="oai-link-group">
              <strong>空间</strong>
              <a href="/login">企业协同</a>
              <a href="/login">文件管理</a>
              <a href="/login">留存策略</a>
            </div>
            <div class="oai-link-group">
              <strong>入口</strong>
              <a href="/login">登录</a>
              <a href="/register">注册</a>
              <a href="/forgot-password">找回密码</a>
            </div>
          </div>
        </div>
        <div class="oai-footer-bottom">
          <p>${BRAND_APP_FOOTER}</p>
        </div>
      </div>
    </footer>
  </div>`;

  bindInteractiveShowcase(root);
  bindMobileNav(root);
}

/**
 * 绑定移动端顶栏菜单展开/收起
 * @param root 根容器
 */
function bindMobileNav(root: HTMLElement): void {
  const header = root.querySelector<HTMLElement>('.oai-header');
  const toggle = root.querySelector<HTMLButtonElement>('.oai-nav-toggle');
  const nav = root.querySelector<HTMLElement>('.oai-nav');
  if (!header || !toggle || !nav) return;

  const setMenuOpen = (open: boolean): void => {
    header.classList.toggle('is-menu-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute('aria-label', open ? '关闭菜单' : '打开菜单');
  };

  toggle.addEventListener('click', () => {
    setMenuOpen(!header.classList.contains('is-menu-open'));
  });

  nav.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => setMenuOpen(false));
  });

  document.addEventListener('click', (event) => {
    if (!header.contains(event.target as Node)) {
      setMenuOpen(false);
    }
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth > 768) {
      setMenuOpen(false);
    }
  });
}

/**
 * 绑定交互式演示切换逻辑
 * @param root 根容器
 */
function bindInteractiveShowcase(root: HTMLElement): void {
  const tabs = root.querySelectorAll<HTMLButtonElement>('.oai-demo-tab');
  const panels = root.querySelectorAll<HTMLElement>('.oai-demo-panel');

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.demoTarget;
      if (!target) return;

      tabs.forEach((item) => item.classList.toggle('is-active', item === tab));
      panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.id === `demo-${target}`);
      });
    });
  });
}
